// CreateDocumentMenu — the "+ Create Document" action menu (Phase 2B).
//
// Opened from the document-management header beside "Upload Document". Every
// entry navigates to a REAL route with a working backend action — no
// decorative buttons.
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FileText, FileSpreadsheet, LayoutTemplate, Upload, X } from "lucide-react";
import "./CreateDocumentMenu.css";

const ITEMS = [
  {
    key: "word",
    label: "Blank Word Document",
    desc: "Rich text document (DOCX/PDF export)",
    icon: FileText,
    to: "/studio/word/new",
  },
  {
    key: "sheet",
    label: "Blank Spreadsheet",
    desc: "Formulas, multiple sheets (XLSX export)",
    icon: FileSpreadsheet,
    to: "/studio/sheet/new",
  },
  {
    key: "template",
    label: "Create From Template",
    desc: "Use an official published template",
    icon: LayoutTemplate,
    to: "/studio/templates",
  },
  {
    key: "upload",
    label: "Upload Existing Document",
    desc: "Import PDF, DOCX, XLSX or PowerPoint files",
    icon: Upload,
    to: "/studio/import",
  },
];

const CreateDocumentMenu = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const returnFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => closeRef.current?.focus());

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => returnFocusRef.current?.focus?.());
    };
  }, [open]);

  if (!open) return null;

  const go = (to) => {
    onClose?.();
    navigate(to);
  };

  return createPortal(
    <div className="wv-create-modal__backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose?.();
    }}>
      <div
        ref={dialogRef}
        className="wv-create-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wv-create-modal-title"
      >
        <header className="wv-create-modal__header">
          <div>
            <h2 id="wv-create-modal-title">Create Document</h2>
            <p>Choose how you want to start.</p>
          </div>
          <button ref={closeRef} type="button" className="wv-create-modal__close" aria-label="Close Create Document" onClick={onClose}>
            <X size={16} />
          </button>
        </header>
        <div className="wv-create-modal__grid">
          {ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.key} type="button" className="wv-create-modal__card" data-assist={item.desc} onClick={() => go(item.to)}>
                <span className="wv-create-modal__icon" aria-hidden="true"><Icon size={22} /></span>
                <span className="wv-create-modal__label">{item.label}</span>
                <span className="wv-create-modal__description">{item.desc}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CreateDocumentMenu;
